import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Store, MapPin, Phone, Mail, Globe, Clock, Plus, X, AlertTriangle, Star, Settings, Users, Calendar, Building2, ExternalLink, Trash2, User } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { getApiUrl } from '../utils/api';
import { formatPhoneAsYouType, inferPhoneCountry, parseInternationalPhone, type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { ShopOpeningHoursEditor } from '../components/ShopOpeningHoursEditor';
import { normalizeShopOpeningHoursForm, type ShopOpeningHoursForm } from '../utils/openingHours';

interface ShopFormData {
  id: string;
  name: string;
  legalName?: string;
  siret?: string;
  companyId?: string;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
    coordinates?: {
      latitude: number;
      longitude: number;
    };
  };
  phone: string;
  phoneCountry?: string;
  email: string;
  website: string;
  socialMedia: {
    instagram: string;
    facebook: string;
    tiktok: string;
  };
  openingHours: ShopOpeningHoursForm;
  services: Array<{
    serviceId: string;
    available: boolean;
    customPricing?: number;
  }>;
  facilities: Array<{
    type: string;
    description: string;
    available: boolean;
  }>;
  policies: {
    cancellation: string;
    deposit: string;
    aftercare: string;
    photography: string;
  };
  timezone: string;
  defaultSlotSizeMinutes: number;
  isActive: boolean;
  onlineBookingEnabled?: boolean;
  allowOnlineBookingIfBackofficeBooking?: boolean;
  cancellationNotifierAdminIds?: string[];
  createdAt: string;
  updatedAt: string;
}

interface AdminOption {
  id: string;
  name: string;
  email: string;
  role?: string;
}

interface PiercingOffering {
  id: string;
  name: string;
  bodyZoneId: string;
  bodyZoneName: string;
  bodyZoneCategory: string;
  itemCollectionId?: string;
  itemCollectionName?: string;
  basePrice: number;
  estimatedDuration: number;
  description?: string;
  isOffered: boolean;
  isAvailable: boolean;
  customPrice?: number;
  customDurationMinutes?: number | null;
  customMinorDurationMinutes?: number | null;
  specialNotes?: string;
}

interface ServiceOffering {
  id: string;
  name: string;
  description: string;
  category: string;
  subcategory?: string;
  basePrice: number;
  estimatedDuration: number;
  onlineBookingEnabled: boolean;
  isOffered: boolean;
  isAvailable: boolean;
  customPrice?: number;
  specialNotes?: string;
}

interface PractitionerAssociation {
  id: string;
  practitionerId: string;
  practitionerFirstName: string;
  practitionerLastName: string;
  practitionerEmail: string;
  relationshipType: 'FREELANCE' | 'EMPLOYEE' | 'RENTAL' | 'PARTNERSHIP';
  hourlyRate?: number;
  commissionRate?: number;
  isActive: boolean;
  startDate: string;
  endDate?: string;
}

interface Practitioner {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

type ShopTabKey = 'general' | 'schedule' | 'facilities';

function stableStringify(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  if (value && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    return `{${Object.keys(obj).sort().map((key) => `${JSON.stringify(key)}:${stableStringify(obj[key])}`).join(',')}}`;
  }
  return JSON.stringify(value ?? null);
}

function normOptionalNumber(value: unknown): number | null {
  if (value === '' || value === undefined || value === null) return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function shopTabSnapshots(
  shop: ShopFormData,
  phoneE164: string | null,
  phoneCountry: string,
): Record<ShopTabKey, string> {
  return {
    general: stableStringify({
      name: shop.name ?? '',
      legalName: shop.legalName ?? '',
      siret: shop.siret ?? '',
      companyId: shop.companyId ?? '',
      address: {
        street: shop.address?.street ?? '',
        city: shop.address?.city ?? '',
        postalCode: shop.address?.postalCode ?? '',
        country: shop.address?.country ?? '',
      },
      phone: phoneE164 ?? '',
      phoneCountry: phoneCountry ?? '',
      email: shop.email ?? '',
      website: shop.website ?? '',
      isActive: shop.isActive === true,
      onlineBookingEnabled: shop.onlineBookingEnabled === true,
      allowOnlineBookingIfBackofficeBooking: shop.allowOnlineBookingIfBackofficeBooking === true,
    }),
    schedule: stableStringify({
      openingHours: shop.openingHours ?? {},
      timezone: shop.timezone || 'Europe/Paris',
      defaultSlotSizeMinutes: shop.defaultSlotSizeMinutes || 15,
      socialMedia: {
        instagram: shop.socialMedia?.instagram ?? '',
        facebook: shop.socialMedia?.facebook ?? '',
        tiktok: shop.socialMedia?.tiktok ?? '',
      },
    }),
    facilities: stableStringify({
      facilities: shop.facilities ?? [],
      policies: {
        cancellation: shop.policies?.cancellation ?? '',
        deposit: shop.policies?.deposit ?? '',
        aftercare: shop.policies?.aftercare ?? '',
        photography: shop.policies?.photography ?? '',
      },
      cancellationNotifierAdminIds: [...(shop.cancellationNotifierAdminIds ?? [])].sort(),
    }),
  };
}

function piercingSnapshot(offerings: PiercingOffering[]): string {
  return stableStringify(
    offerings
      .map((offering) => ({
        id: offering.id,
        isOffered: offering.isOffered === true,
        isAvailable: offering.isAvailable === true,
        customPrice: normOptionalNumber(offering.customPrice),
        customDurationMinutes: normOptionalNumber(offering.customDurationMinutes),
        customMinorDurationMinutes: normOptionalNumber(offering.customMinorDurationMinutes),
        specialNotes: offering.specialNotes ?? '',
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  );
}

function serviceSnapshot(services: ServiceOffering[]): string {
  return stableStringify(
    services
      .map((service) => ({
        id: service.id,
        isOffered: service.isOffered === true,
        isAvailable: service.isAvailable === true,
        customPrice: normOptionalNumber(service.customPrice),
        specialNotes: service.specialNotes ?? '',
      }))
      .sort((a, b) => a.id.localeCompare(b.id)),
  );
}

export function ShopEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const [shop, setShop] = useState<ShopFormData | null>(null);
  const [piercingOfferings, setPiercingOfferings] = useState<PiercingOffering[]>([]);
  const [serviceOfferings, setServiceOfferings] = useState<ServiceOffering[]>([]);
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [associatedPractitioners, setAssociatedPractitioners] = useState<PractitionerAssociation[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const [shopBaselines, setShopBaselines] = useState<Record<ShopTabKey, string> | null>(null);
  const [piercingBaseline, setPiercingBaseline] = useState<string | null>(null);
  const [serviceBaseline, setServiceBaseline] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const saveLock = useRef(false);
  const [companies, setCompanies] = useState<Array<{id: string; name: string}>>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [showAddPractitionerModal, setShowAddPractitionerModal] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);
  const [selectedPractitionerId, setSelectedPractitionerId] = useState<string>('');
  const [newAssociation, setNewAssociation] = useState({
    relationshipType: 'FREELANCE' as const,
    hourlyRate: '',
    commissionRate: '',
  });
  const [adminOptions, setAdminOptions] = useState<AdminOption[]>([]);

  useEffect(() => {
    loadCompanies();
    if (id) {
      fetchShop(id);
      fetchPiercingOfferings(id);
      fetchShopServices(id);
      fetchPractitioners();
      fetchAssociatedPractitioners(id);
    }
  }, [id]);

  const fetchShop = async (shopId: string) => {
    try {
      setLoading(true);
      setError(null);
      setErrorDetails([]);
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/shops/${shopId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        const shopData = data.data.shop;
        const inferredPhoneCountry =
          (shopData.phoneCountry as SupportedPhoneCountry | undefined) ||
          parseInternationalPhone(shopData.phone || '', 'FR').country ||
          inferPhoneCountry(shopData.address?.country || 'France');
        const parsedPhone = parseInternationalPhone(shopData.phone || '', inferredPhoneCountry);
        const resolvedPhoneCountry = parsedPhone.country ?? inferredPhoneCountry;

        const loadedPhone = parsedPhone.isValid ? parsedPhone.e164 : null;
        const formShop: ShopFormData = {
          ...shopData,
          phoneCountry: resolvedPhoneCountry,
          phone: formatPhoneAsYouType(shopData.phone || '', resolvedPhoneCountry),
          services: Array.isArray(shopData.services) ? shopData.services : [],
          facilities: Array.isArray(shopData.facilities) ? shopData.facilities : [],
          openingHours: normalizeShopOpeningHoursForm(shopData.openingHours),
          socialMedia: shopData.socialMedia || { instagram: '', facebook: '', tiktok: '' },
          policies: shopData.policies || { cancellation: '', deposit: '', aftercare: '', photography: '' },
          address: shopData.address || { street: '', city: '', postalCode: '', country: 'France' },
          legalName: shopData.legalName || '',
          siret: shopData.siret || '',
          timezone: shopData.timezone || 'Europe/Paris',
          defaultSlotSizeMinutes: shopData.defaultSlotSizeMinutes || 15,
          allowOnlineBookingIfBackofficeBooking: shopData.allowOnlineBookingIfBackofficeBooking === true,
          cancellationNotifierAdminIds: Array.isArray(shopData.cancellationNotifierAdminIds)
            ? shopData.cancellationNotifierAdminIds
            : [],
        };
        setPhoneCountry(resolvedPhoneCountry);
        setPhoneE164(loadedPhone);
        setPhoneIsValid(parsedPhone.isValid);
        setShop(formShop);
        setShopBaselines(shopTabSnapshots(formShop, loadedPhone, resolvedPhoneCountry));
        if (Array.isArray(shopData.availableCancellationNotifierAdmins)) {
          setAdminOptions(shopData.availableCancellationNotifierAdmins);
        }
      } else {
        setError(data.message || 'Échec du chargement du shop');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Fetch shop error:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const fetchPiercingOfferings = async (shopId: string) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/shops/${shopId}/piercing-offerings`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const offerings = data.data.piercingOfferings || [];
          setPiercingOfferings(offerings);
          setPiercingBaseline(piercingSnapshot(offerings));
        }
      }
    } catch (error) {
      console.error('Fetch piercing offerings error:', error);
    }
  };

  const fetchShopServices = async (shopId: string) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(getApiUrl(`/api/admin/shops/${shopId}/services`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const services = data.data.services || [];
          setServiceOfferings(services);
          setServiceBaseline(serviceSnapshot(services));
        }
      }
    } catch (error) {
      console.error('Fetch shop services error:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/companies?limit=100'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setCompanies(data.data.companies.map((c: any) => ({
            id: c.id,
            name: c.name
          })));
        }
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchPractitioners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/practitioners?limit=1000'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPractitioners(data.data.practitioners);
        }
      }
    } catch (error) {
      console.error('Fetch practitioners error:', error);
    }
  };

  const fetchAssociatedPractitioners = async (shopId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/admin/shops/${shopId}/practitioners`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAssociatedPractitioners(data.data.practitioners || []);
        }
      }
    } catch (error) {
      console.error('Fetch associated practitioners error:', error);
    }
  };

  const handleAddPractitionerAssociation = async () => {
    if (!selectedPractitionerId || !id) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/planning/practitioners/${selectedPractitionerId}/shops/${id}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          relationshipType: newAssociation.relationshipType,
          hourlyRate: newAssociation.hourlyRate ? parseFloat(newAssociation.hourlyRate) : null,
          commissionRate: newAssociation.commissionRate ? parseFloat(newAssociation.commissionRate) : null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh associated practitioners
          await fetchAssociatedPractitioners(id);
          setShowAddPractitionerModal(false);
          setSelectedPractitionerId('');
          setNewAssociation({
            relationshipType: 'FREELANCE',
            hourlyRate: '',
            commissionRate: '',
          });
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Échec de l\'ajout de l\'artiste au shop');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Add practitioner association error:', error);
      setError('Erreur de connexion au serveur');
    }
  };

  const handleRemovePractitionerAssociation = async (practitionerId: string) => {
    if (!id) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir retirer cet artiste du shop ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`/api/planning/practitioners/${practitionerId}/shops/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh associated practitioners
        await fetchAssociatedPractitioners(id);
      } else {
        const data = await response.json();
        setError(data.message || 'Échec du retrait de l\'artiste du shop');
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          setErrorDetails(data.errors);
        }
      }
    } catch (error) {
      console.error('Remove practitioner association error:', error);
      setError('Erreur de connexion au serveur');
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setShop(prev => {
      if (!prev) return prev;
      const keys = field.split('.');
      const newData: any = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...(current[keys[i]] || {}) };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const tabs = [
    {
      id: 'general',
      label: 'Informations Générales',
      icon: Store,
      description: 'Nom, contact et adresse'
    },
    {
      id: 'schedule',
      label: 'Horaires & Réseaux',
      icon: Calendar,
      description: 'Horaires d\'ouverture et réseaux sociaux'
    },
    {
      id: 'facilities',
      label: 'Équipements & Politiques',
      icon: Settings,
      description: 'Équipements et politiques du shop'
    },
    {
      id: 'practitioners',
      label: 'Artistes Associés',
      icon: Users,
      description: 'Artistes travaillant dans ce shop'
    },
    {
      id: 'piercings',
      label: 'Offres de Piercing',
      icon: Star,
      description: 'Types de piercing proposés'
    },
    {
      id: 'services',
      label: 'Services',
      icon: Settings,
      description: 'Services proposés par le shop'
    }
  ];

  const addFacility = () => {
    if (!shop) return;
    
    setShop(prev => prev ? {
      ...prev,
      facilities: [
        ...prev.facilities,
        {
          type: '',
          description: '',
          available: true
        }
      ]
    } : prev);
  };

  const removeFacility = (index: number) => {
    if (!shop) return;
    
    setShop(prev => prev ? {
      ...prev,
      facilities: prev.facilities.filter((_, i) => i !== index)
    } : prev);
  };

  const updateFacility = (index: number, field: string, value: any) => {
    if (!shop) return;
    
    setShop(prev => prev ? {
      ...prev,
      facilities: prev.facilities.map((facility, i) => 
        i === index ? { ...facility, [field]: value } : facility
      )
    } : prev);
  };

  const handlePiercingOfferingChange = (piercingTypeId: string, field: string, value: any) => {
    setPiercingOfferings(prev => 
      prev.map(offering => 
        offering.id === piercingTypeId 
          ? { ...offering, [field]: value }
          : offering
      )
    );
  };

  const handleServiceChange = (serviceId: string, field: string, value: any) => {
    setServiceOfferings(prev => 
      prev.map(service => 
        service.id === serviceId 
          ? { ...service, [field]: value }
          : service
      )
    );
  };

  const shopSnapshots = useMemo(
    () => (shop ? shopTabSnapshots(shop, phoneE164, phoneCountry) : null),
    [shop, phoneE164, phoneCountry],
  );
  const currentPiercingSnapshot = useMemo(() => piercingSnapshot(piercingOfferings), [piercingOfferings]);
  const currentServiceSnapshot = useMemo(() => serviceSnapshot(serviceOfferings), [serviceOfferings]);
  const dirtyTabs = {
    general: Boolean(shopSnapshots && shopBaselines && shopSnapshots.general !== shopBaselines.general),
    schedule: Boolean(shopSnapshots && shopBaselines && shopSnapshots.schedule !== shopBaselines.schedule),
    facilities: Boolean(shopSnapshots && shopBaselines && shopSnapshots.facilities !== shopBaselines.facilities),
    practitioners: false,
    piercings: piercingBaseline !== null && currentPiercingSnapshot !== piercingBaseline,
    services: serviceBaseline !== null && currentServiceSnapshot !== serviceBaseline,
  };
  const hasUnsavedChanges = dirtyTabs.general || dirtyTabs.schedule || dirtyTabs.facilities || dirtyTabs.piercings || dirtyTabs.services;

  const handleSave = async () => {
    if (!shop || !shopSnapshots || saving || saveLock.current || !hasUnsavedChanges) return;

    const generalBlocked = dirtyTabs.general && (!shop.phone.trim() || !phoneIsValid || !phoneE164);
    const shopPayload: Record<string, unknown> = {};
    if (dirtyTabs.general && !generalBlocked) {
      Object.assign(shopPayload, {
        name: shop.name,
        legalName: shop.legalName || null,
        siret: shop.siret || null,
        companyId: shop.companyId || null,
        address: shop.address,
        phone: phoneE164,
        email: shop.email,
        website: shop.website || null,
        isActive: !!shop.isActive,
        onlineBookingEnabled: !!shop.onlineBookingEnabled,
        allowOnlineBookingIfBackofficeBooking: shop.allowOnlineBookingIfBackofficeBooking === true,
      });
    }
    if (dirtyTabs.schedule) {
      Object.assign(shopPayload, {
        openingHours: shop.openingHours,
        socialMedia: shop.socialMedia,
        timezone: shop.timezone || 'Europe/Paris',
        defaultSlotSizeMinutes: shop.defaultSlotSizeMinutes || 15,
      });
    }
    if (dirtyTabs.facilities) {
      Object.assign(shopPayload, {
        facilities: shop.facilities,
        policies: shop.policies,
        cancellationNotifierAdminIds: shop.cancellationNotifierAdminIds || [],
      });
    }

    const savePiercings = dirtyTabs.piercings;
    const saveServices = dirtyTabs.services;
    const savedGeneral = shopSnapshots.general;
    const savedSchedule = shopSnapshots.schedule;
    const savedFacilities = shopSnapshots.facilities;
    const savedPiercings = currentPiercingSnapshot;
    const savedServices = currentServiceSnapshot;
    const piercingBody = piercingOfferings.map(offering => ({
      piercingTypeId: offering.id,
      isOffered: offering.isOffered,
      isAvailable: offering.isAvailable,
      customPrice: offering.customPrice,
      customDurationMinutes: offering.customDurationMinutes,
      customMinorDurationMinutes: offering.customMinorDurationMinutes,
      specialNotes: offering.specialNotes,
    }));
    const serviceBody = serviceOfferings.map(service => ({
      serviceId: service.id,
      isOffered: service.isOffered,
      isAvailable: service.isAvailable,
      customPrice: service.customPrice,
      specialNotes: service.specialNotes,
    }));

    saveLock.current = true;
    setSaving(true);
    setError(null);
    setErrorDetails([]);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    const failures: string[] = [];
    const details: string[] = [];
    let shopSaved = false;
    let piercingsSaved = false;
    let servicesSaved = false;

    if (generalBlocked) {
      failures.push('Le numéro de téléphone est invalide pour le pays sélectionné.');
    }

    try {
      if (Object.keys(shopPayload).length > 0) {
        const shopResponse = await fetch(getApiUrl(`/api/admin/shops/${shop.id}`), {
          method: 'PUT',
          headers,
          body: JSON.stringify(shopPayload),
        });
        const shopData = await shopResponse.json();
        if (!shopData.success) {
          failures.push(shopData.message || 'Échec de la sauvegarde du shop');
          if (Array.isArray(shopData.errors)) details.push(...shopData.errors);
        } else {
          shopSaved = true;
          setShopBaselines((prev) => prev ? {
            ...prev,
            ...(dirtyTabs.general && !generalBlocked ? { general: savedGeneral } : {}),
            ...(dirtyTabs.schedule ? { schedule: savedSchedule } : {}),
            ...(dirtyTabs.facilities ? { facilities: savedFacilities } : {}),
          } : prev);
        }
      }

      if (savePiercings) {
        const offeringsResponse = await fetch(getApiUrl(`/api/admin/shops/${shop.id}/piercing-offerings`), {
          method: 'PUT',
          headers,
          body: JSON.stringify({ offerings: piercingBody }),
        });
        const offeringsData = await offeringsResponse.json();
        if (!offeringsData.success) {
          failures.push(offeringsData.message || 'Échec de la sauvegarde des offres de piercing');
          if (Array.isArray(offeringsData.errors)) details.push(...offeringsData.errors);
        } else {
          piercingsSaved = true;
          setPiercingBaseline(savedPiercings);
        }
      }

      if (saveServices) {
        const servicesResponse = await fetch(getApiUrl(`/api/admin/shops/${shop.id}/services`), {
          method: 'PUT',
          headers,
          body: JSON.stringify({ services: serviceBody }),
        });
        const servicesData = await servicesResponse.json();
        if (!servicesData.success) {
          failures.push(servicesData.message || 'Échec de la sauvegarde des services du shop');
          if (Array.isArray(servicesData.errors)) details.push(...servicesData.errors);
        } else {
          servicesSaved = true;
          setServiceBaseline(savedServices);
        }
      }

      if (failures.length > 0) {
        const partial = shopSaved || piercingsSaved || servicesSaved;
        setError(`${failures.join(' ')}${partial ? ' Les autres modifications ont été enregistrées.' : ''}`);
        setErrorDetails(details);
      } else {
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded z-50';
        successMessage.textContent = 'Modifications enregistrées';
        document.body.appendChild(successMessage);
        setTimeout(() => {
          if (successMessage.parentNode) successMessage.parentNode.removeChild(successMessage);
        }, 3000);
      }
    } catch (saveError) {
      console.error('Save shop error:', saveError);
      setError('Erreur de connexion au serveur');
    } finally {
      saveLock.current = false;
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?')) {
        navigate('/shops');
      }
    } else {
      navigate('/shops');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Chargement du shop...</span>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
            <span className="text-red-800 font-medium">
              {error || 'Shop non trouvé'}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate('/shops')}
          className="mt-4 btn-secondary"
        >
          Retour à la liste des shops
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors border border-gray-300 shadow-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Retour à la liste</span>
          </button>
          
          <div>
            <h1 className="text-2xl font-display font-bold text-textPrimary flex items-center space-x-2">
              <span>Modifier: {shop.name}</span>
              <HelpIcon 
                docPath="04-boutiques#04-5-modification" 
                tooltip="Documentation : Modification d'une boutique"
              />
            </h1>
            {hasUnsavedChanges && (
              <p className="text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded mt-1">
                Modifications non sauvegardées
              </p>
            )}
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>

      {/* Inactive shop notice */}
      {!shop.isActive && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start justify-between gap-4">
          <div className="flex items-start space-x-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-amber-900 font-medium">Ce shop est inactif</p>
              <p className="text-sm text-amber-800 mt-1">
                Il reste modifiable ici, mais il n'apparaît ni dans les réservations ni dans le
                sélecteur de shop.
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setActiveTab('general');
              handleInputChange('isActive', true);
            }}
            className="btn-secondary whitespace-nowrap"
          >
            Réactiver
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-800 font-medium">{error}</p>
          {errorDetails.length > 0 && (
            <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
              {errorDetails.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Form */}
      <div className="space-y-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className={`mr-2 h-5 w-5 ${
                    activeTab === tab.id ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`} />
                  <div className="text-left">
                    <div className="flex items-center gap-1">
                      <span>{tab.label}</span>
                      {dirtyTabs[tab.id as keyof typeof dirtyTabs] && (
                        <span title="Modifications non sauvegardées" className="inline-flex">
                          <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" aria-hidden="true" />
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 hidden sm:block">{tab.description}</div>
                  </div>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-8">
          {/* General Information Tab */}
          {activeTab === 'general' && (
            <>
              {/* Basic Information */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                    <Store className="w-5 h-5 text-primary-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Informations Générales</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Nom du Shop *
                    </label>
                    <input
                      type="text"
                      value={shop.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Raison sociale
                    </label>
                    <input
                      type="text"
                      value={shop.legalName || ''}
                      onChange={(e) => handleInputChange('legalName', e.target.value || undefined)}
                      className="input-field"
                      placeholder="Raison sociale de l'établissement"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      SIRET
                    </label>
                    <input
                      type="text"
                      value={shop.siret || ''}
                      onChange={(e) => handleInputChange('siret', e.target.value || undefined)}
                      className="input-field"
                      placeholder="123 456 789 00012"
                      maxLength={17}
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      <Building2 className="w-4 h-4 inline mr-1" />
                      Compagnie
                    </label>
                    <div className="flex items-center space-x-2">
                      <select
                        value={shop.companyId || ''}
                        onChange={(e) => handleInputChange('companyId', e.target.value || undefined)}
                        className="input-field flex-1"
                        disabled={loadingCompanies}
                      >
                        <option value="">Aucune compagnie</option>
                        {companies.map(company => (
                          <option key={company.id} value={company.id}>
                            {company.name}
                          </option>
                        ))}
                      </select>
                      {shop.companyId && (
                        <button
                          type="button"
                          onClick={() => navigate(`/companies/${shop.companyId}`)}
                          className="btn-secondary flex items-center space-x-1 whitespace-nowrap"
                          title="Voir la compagnie"
                        >
                          <ExternalLink className="w-4 h-4" />
                          <span className="hidden sm:inline">Voir</span>
                        </button>
                      )}
                    </div>
                    {loadingCompanies && (
                      <p className="mt-1 text-sm text-gray-500">Chargement des compagnies...</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      <Phone className="w-4 h-4 inline mr-1" />
                      Téléphone *
                    </label>
                    <InternationalPhoneInput
                      value={shop.phone}
                      country={phoneCountry}
                      required
                      onChange={(result) => {
                        setPhoneCountry(result.country);
                        setPhoneE164(result.e164Value);
                        setPhoneIsValid(result.isValid);
                        handleInputChange('phone', result.displayValue);
                        handleInputChange('phoneCountry', result.country);
                        if (result.isValid) {
                          setError((prev) =>
                            prev?.startsWith('Le numéro de téléphone est invalide') ? null : prev,
                          );
                        }
                      }}
                      inputClassName="input-field"
                      selectClassName="input-field"
                      placeholder="+33 6 12 34 56 78"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email *
                    </label>
                    <input
                      type="email"
                      value={shop.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      <Globe className="w-4 h-4 inline mr-1" />
                      Site Web
                    </label>
                    <input
                      type="url"
                      value={shop.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-accent-100 rounded-lg">
                    <MapPin className="w-5 h-5 text-accent-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Adresse</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="md:col-span-2 lg:col-span-3">
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Adresse *
                    </label>
                    <input
                      type="text"
                      value={shop.address.street}
                      onChange={(e) => handleInputChange('address.street', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Ville *
                    </label>
                    <input
                      type="text"
                      value={shop.address.city}
                      onChange={(e) => handleInputChange('address.city', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Code Postal *
                    </label>
                    <input
                      type="text"
                      value={shop.address.postalCode}
                      onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Pays *
                    </label>
                    <select
                      value={shop.address.country}
                      onChange={(e) => handleInputChange('address.country', e.target.value)}
                      className="input-field"
                    >
                      <option value="France">France</option>
                      <option value="Belgium">Belgique</option>
                      <option value="Switzerland">Suisse</option>
                      <option value="Canada">Canada</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <h2 className="text-xl font-semibold text-textPrimary">Statut</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={shop.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-textPrimary">Shop actif</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Les shops inactifs n'apparaîtront pas dans les options de réservation
                    </p>
                  </div>
                  
                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={shop.onlineBookingEnabled || false}
                        onChange={(e) => handleInputChange('onlineBookingEnabled', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-textPrimary">Réservation en ligne activée</span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Permet aux clients de réserver en ligne via le frontoffice
                    </p>
                  </div>

                  <div>
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={shop.allowOnlineBookingIfBackofficeBooking === true}
                        onChange={(e) => handleInputChange('allowOnlineBookingIfBackofficeBooking', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-textPrimary">
                        Autoriser la réservation piercing en ligne sur un créneau déjà réservé au backoffice
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Si cette option est désactivée, un créneau occupé par un rendez-vous backoffice n&apos;est plus proposé à la réservation piercing en ligne. La limite de réservations en ligne par créneau reste appliquée dans les deux cas.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Schedule & Social Media Tab */}
          {activeTab === 'schedule' && (
            <>
              {/* Opening Hours */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Horaires d'Ouverture</h2>
                </div>

                <ShopOpeningHoursEditor
                  value={shop.openingHours}
                  onChange={(next) => handleInputChange('openingHours', next)}
                />

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-textPrimary mb-4 uppercase tracking-wide">Paramètres de planification</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Fuseau horaire
                      </label>
                      <select
                        value={shop.timezone || 'Europe/Paris'}
                        onChange={(e) => handleInputChange('timezone', e.target.value)}
                        className="input-field"
                      >
                        <option value="Europe/Paris">Europe/Paris (France)</option>
                        <option value="Indian/Reunion">Indian/Reunion (La Réunion)</option>
                        <option value="Europe/Brussels">Europe/Brussels (Belgique)</option>
                        <option value="Europe/Zurich">Europe/Zurich (Suisse)</option>
                        <option value="Europe/London">Europe/London (Royaume-Uni)</option>
                        <option value="America/Montreal">America/Montreal (Canada - Québec)</option>
                        <option value="America/Toronto">America/Toronto (Canada - Ontario)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Utilisé pour l'affichage des créneaux horaires aux clients
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        Taille des créneaux par défaut (minutes)
                      </label>
                      <select
                        value={shop.defaultSlotSizeMinutes || 15}
                        onChange={(e) => handleInputChange('defaultSlotSizeMinutes', parseInt(e.target.value, 10))}
                        className="input-field"
                      >
                        <option value={10}>10 minutes</option>
                        <option value={15}>15 minutes</option>
                        <option value={20}>20 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Détermine l'intervalle entre les créneaux proposés aux clients lors de la réservation en ligne
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                    <Globe className="w-5 h-5 text-purple-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Réseaux Sociaux</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Instagram
                    </label>
                    <input
                      type="text"
                      value={shop.socialMedia.instagram}
                      onChange={(e) => handleInputChange('socialMedia.instagram', e.target.value)}
                      className="input-field"
                      placeholder="@digitaltattoo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Facebook
                    </label>
                    <input
                      type="text"
                      value={shop.socialMedia.facebook}
                      onChange={(e) => handleInputChange('socialMedia.facebook', e.target.value)}
                      className="input-field"
                      placeholder="digitaltattoo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      TikTok
                    </label>
                    <input
                      type="text"
                      value={shop.socialMedia.tiktok}
                      onChange={(e) => handleInputChange('socialMedia.tiktok', e.target.value)}
                      className="input-field"
                      placeholder="@digitaltattoo"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Facilities & Policies Tab */}
          {activeTab === 'facilities' && (
            <>
              {/* Facilities */}
              <div className="card">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                      <Store className="w-5 h-5 text-green-600" />
                    </div>
                    <h2 className="text-xl font-semibold text-textPrimary">Équipements</h2>
                  </div>
                  <button
                    type="button"
                    onClick={addFacility}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Ajouter un équipement</span>
                  </button>
                </div>

                {shop.facilities.map((facility, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-medium text-textPrimary">Équipement {index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => removeFacility(index)}
                        className="text-red-600 hover:text-red-700 p-1"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-2">
                          Type
                        </label>
                        <input
                          type="text"
                          value={facility.type}
                          onChange={(e) => updateFacility(index, 'type', e.target.value)}
                          className="input-field"
                          placeholder="Salle privée"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-textPrimary mb-2">
                          Description
                        </label>
                        <input
                          type="text"
                          value={facility.description}
                          onChange={(e) => updateFacility(index, 'description', e.target.value)}
                          className="input-field"
                          placeholder="3 salles privées pour tatouage"
                        />
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={facility.available}
                            onChange={(e) => updateFacility(index, 'available', e.target.checked)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          <span className="text-sm text-textPrimary">Disponible</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}

                {shop.facilities.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Store className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <p>Aucun équipement ajouté</p>
                    <p className="text-sm">Cliquez sur "Ajouter un équipement" pour commencer</p>
                  </div>
                )}
              </div>

              {/* Policies */}
              <div className="card">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 bg-yellow-100 rounded-lg">
                    <Globe className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Politiques du Shop</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Politique d'Annulation
                    </label>
                    <textarea
                      value={shop.policies.cancellation}
                      onChange={(e) => handleInputChange('policies.cancellation', e.target.value)}
                      rows={2}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Politique d'Acompte
                    </label>
                    <textarea
                      value={shop.policies.deposit}
                      onChange={(e) => handleInputChange('policies.deposit', e.target.value)}
                      rows={2}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Politique de Soins
                    </label>
                    <textarea
                      value={shop.policies.aftercare}
                      onChange={(e) => handleInputChange('policies.aftercare', e.target.value)}
                      rows={2}
                      className="input-field"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-textPrimary mb-2">
                      Politique de Photographie
                    </label>
                    <textarea
                      value={shop.policies.photography}
                      onChange={(e) => handleInputChange('policies.photography', e.target.value)}
                      rows={2}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                    <Mail className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-textPrimary">
                      Responsables annulation client
                    </h3>
                    <p className="text-sm text-gray-500">
                      Admins / super-admins notifiés lorsqu&apos;un client annule en ligne.
                      Si aucun n&apos;est sélectionné, tous les admins du shop sont notifiés.
                    </p>
                  </div>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {adminOptions.length === 0 && (
                    <p className="text-sm text-gray-500">
                      Aucun admin disponible pour ce shop. Assignez d&apos;abord des admins au shop.
                    </p>
                  )}
                  {adminOptions.map((admin) => {
                    const selected = (shop.cancellationNotifierAdminIds || []).includes(admin.id);
                    return (
                      <label
                        key={admin.id}
                        className="flex items-start gap-3 rounded-md px-2 py-2 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded"
                          checked={selected}
                          onChange={(e) => {
                            const current = shop.cancellationNotifierAdminIds || [];
                            const next = e.target.checked
                              ? [...current, admin.id]
                              : current.filter((x) => x !== admin.id);
                            handleInputChange('cancellationNotifierAdminIds', next);
                          }}
                        />
                        <span className="text-sm text-textPrimary">
                          <span className="font-medium">{admin.name}</span>
                          <span className="block text-xs text-gray-500">
                            {admin.email}
                            {admin.role ? ` · ${admin.role}` : ''}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Practitioners Tab */}
          {activeTab === 'practitioners' && (
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-textPrimary">Artistes Associés</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddPractitionerModal(true)}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Ajouter un artiste</span>
                </button>
              </div>

              {associatedPractitioners.length > 0 ? (
                <div className="space-y-4">
                  {associatedPractitioners.map((association) => (
                    <div key={association.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <User className="w-5 h-5 text-gray-400" />
                            <h3 className="font-medium text-textPrimary">
                              {association.practitionerFirstName} {association.practitionerLastName}
                            </h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              association.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {association.isActive ? 'Actif' : 'Inactif'}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {association.relationshipType === 'FREELANCE' && 'Freelance'}
                              {association.relationshipType === 'EMPLOYEE' && 'Employé'}
                              {association.relationshipType === 'RENTAL' && 'Location'}
                              {association.relationshipType === 'PARTNERSHIP' && 'Partenariat'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-2">{association.practitionerEmail}</p>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            {association.hourlyRate && (
                              <div>
                                <span className="text-gray-500">Tarif horaire:</span>
                                <span className="ml-1 font-medium">{association.hourlyRate}€/h</span>
                              </div>
                            )}
                            {association.commissionRate && (
                              <div>
                                <span className="text-gray-500">Commission:</span>
                                <span className="ml-1 font-medium">{association.commissionRate}%</span>
                              </div>
                            )}
                            <div>
                              <span className="text-gray-500">Depuis le:</span>
                              <span className="ml-1 font-medium">
                                {new Date(association.startDate).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                            {association.endDate && (
                              <div>
                                <span className="text-gray-500">Jusqu'au:</span>
                                <span className="ml-1 font-medium">
                                  {new Date(association.endDate).toLocaleDateString('fr-FR')}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => handleRemovePractitionerAssociation(association.practitionerId)}
                          className="text-red-600 hover:text-red-700 p-1 ml-4"
                          title="Retirer cet artiste"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>Aucun artiste associé</p>
                  <p className="text-sm">Cliquez sur "Ajouter un artiste" pour commencer</p>
                </div>
              )}
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
                  <Settings className="w-5 h-5 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Services</h2>
              </div>

              {serviceOfferings.length > 0 ? (
                <div className="space-y-6">
                  {/* Group by service category */}
                  {['TATTOO', 'PIERCING', 'CONSULTATION', 'AFTERCARE', 'REMOVAL', 'JEWELRY'].map(category => {
                    const categoryServices = serviceOfferings.filter(service => 
                      service.category === category
                    );
                    
                    if (categoryServices.length === 0) return null;
                    
                    return (
                      <div key={category} className="border border-gray-200 rounded-lg p-4">
                        <h3 className="font-medium text-textPrimary mb-4">
                          {category === 'TATTOO' ? 'Tatouage' : 
                           category === 'PIERCING' ? 'Piercing' :
                           category === 'CONSULTATION' ? 'Consultation' :
                           category === 'AFTERCARE' ? 'Soins' :
                           category === 'REMOVAL' ? 'Suppression' :
                           category === 'JEWELRY' ? 'Bijouterie' : category}
                        </h3>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {categoryServices.map((service) => (
                            <div key={service.id} className="border border-gray-100 rounded-lg p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div>
                                  <h4 className="font-medium text-gray-900">{service.name}</h4>
                                  {service.description && (
                                    <p className="text-sm text-gray-500 mt-1">{service.description}</p>
                                  )}
                                  <p className="text-sm text-gray-600 mt-1">Prix de base: {service.basePrice}€</p>
                                  <p className="text-xs text-gray-500">Durée: {service.estimatedDuration} min</p>
                                </div>
                                <label className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={service.isOffered}
                                    onChange={(e) => handleServiceChange(service.id, 'isOffered', e.target.checked)}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                  />
                                  <span className="ml-2 text-sm text-gray-700">Proposé</span>
                                </label>
                              </div>
                              
                              {service.isOffered && (
                                <div className="space-y-3 border-t pt-3">
                                  <label className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={service.isAvailable}
                                      onChange={(e) => handleServiceChange(service.id, 'isAvailable', e.target.checked)}
                                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                    />
                                    <span className="ml-2 text-sm text-gray-700">Disponible</span>
                                  </label>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Prix personnalisé (€)
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="5"
                                      value={service.customPrice || ''}
                                      onChange={(e) => handleServiceChange(service.id, 'customPrice', e.target.value ? parseFloat(e.target.value) : null)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                      placeholder={`${service.basePrice}`}
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">
                                      Notes spéciales
                                    </label>
                                    <input
                                      type="text"
                                      value={service.specialNotes || ''}
                                      onChange={(e) => handleServiceChange(service.id, 'specialNotes', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                      placeholder="Notes particulières..."
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Settings className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>Chargement des services...</p>
                </div>
              )}
            </div>
          )}

          {/* Piercing Offerings Tab */}
          {activeTab === 'piercings' && (
            <div className="card">
              <div className="flex items-center space-x-3 mb-6">
                <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                  <Star className="w-5 h-5 text-purple-600" />
                </div>
                <h2 className="text-xl font-semibold text-textPrimary">Offres de Piercing</h2>
              </div>

              {piercingOfferings.length > 0 ? (
                <div className="space-y-6">
                  {Array.from(new Set(piercingOfferings.map((o) => o.itemCollectionName || 'Catalogue'))).map((collectionName) => {
                    const inCollection = piercingOfferings.filter((o) => (o.itemCollectionName || 'Catalogue') === collectionName);
                    return (
                      <div key={collectionName} className="space-y-4">
                        <h3 className="text-lg font-semibold text-textPrimary">{collectionName}</h3>
                        {['head', 'oral', 'body', 'genital'].map((category) => {
                          const categoryOfferings = inCollection.filter((offering) =>
                            offering.bodyZoneCategory === category
                          );
                          if (categoryOfferings.length === 0) return null;
                          const categoryLabel =
                            category === 'head' ? 'Tête' :
                            category === 'oral' ? 'Oral' :
                            category === 'body' ? 'Corps' :
                            'Génital';
                          return (
                            <div key={`${collectionName}-${category}`} className="border border-gray-200 rounded-lg p-4">
                              <h4 className="font-medium text-textPrimary mb-4">{categoryLabel}</h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {categoryOfferings.map((offering) => (
                                  <div key={offering.id} className="border border-gray-100 rounded-lg p-4">
                                    <div className="flex items-start justify-between mb-3">
                                      <div>
                                        <h4 className="font-medium text-gray-900">{offering.name}</h4>
                                        <p className="text-sm text-gray-500">{offering.bodyZoneName}</p>
                                        <p className="text-sm text-gray-600">Prix de base: {offering.basePrice}€</p>
                                      </div>
                                      <label className="flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={offering.isOffered}
                                          onChange={(e) => handlePiercingOfferingChange(offering.id, 'isOffered', e.target.checked)}
                                          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                        />
                                        <span className="ml-2 text-sm text-gray-700">Proposé</span>
                                      </label>
                                    </div>
                                    {offering.isOffered && (
                                      <div className="space-y-3 border-t pt-3">
                                        <label className="flex items-center">
                                          <input
                                            type="checkbox"
                                            checked={offering.isAvailable}
                                            onChange={(e) => handlePiercingOfferingChange(offering.id, 'isAvailable', e.target.checked)}
                                            className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                                          />
                                          <span className="ml-2 text-sm text-gray-700">Disponible</span>
                                        </label>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Prix personnalisé (€)</label>
                                          <input
                                            type="number"
                                            min="0"
                                            step="5"
                                            value={offering.customPrice || ''}
                                            onChange={(e) => handlePiercingOfferingChange(offering.id, 'customPrice', e.target.value ? parseFloat(e.target.value) : null)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            placeholder={`${offering.basePrice}`}
                                          />
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                              Durée adulte (min)
                                            </label>
                                            <input
                                              type="number"
                                              min="1"
                                              step="1"
                                              value={offering.customDurationMinutes ?? ''}
                                              onChange={(e) => handlePiercingOfferingChange(
                                                offering.id,
                                                'customDurationMinutes',
                                                e.target.value ? parseInt(e.target.value, 10) : null,
                                              )}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                              placeholder="Défaut shop"
                                            />
                                          </div>
                                          <div>
                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                              Durée mineur (min)
                                            </label>
                                            <input
                                              type="number"
                                              min="1"
                                              step="1"
                                              value={offering.customMinorDurationMinutes ?? ''}
                                              onChange={(e) => handlePiercingOfferingChange(
                                                offering.id,
                                                'customMinorDurationMinutes',
                                                e.target.value ? parseInt(e.target.value, 10) : null,
                                              )}
                                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                              placeholder="Défaut mineur"
                                            />
                                          </div>
                                        </div>
                                        <p className="text-xs text-gray-500">
                                          Laisser vide pour utiliser les durées définies dans les règles de planning du shop.
                                        </p>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">Notes spéciales</label>
                                          <input
                                            type="text"
                                            value={offering.specialNotes || ''}
                                            onChange={(e) => handlePiercingOfferingChange(offering.id, 'specialNotes', e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                            placeholder="Notes particulières..."
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Star className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p>Chargement des offres de piercing...</p>
                </div>
              )}

            </div>
          )}
        </div>
      </div>

      {/* Add Practitioner Modal */}
      {showAddPractitionerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-textPrimary">Ajouter un Artiste</h3>
              <button
                onClick={() => {
                  setShowAddPractitionerModal(false);
                  setSelectedPractitionerId('');
                  setNewAssociation({
                    relationshipType: 'FREELANCE',
                    hourlyRate: '',
                    commissionRate: '',
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Artiste *
                </label>
                <select
                  value={selectedPractitionerId}
                  onChange={(e) => setSelectedPractitionerId(e.target.value)}
                  className="input-field"
                >
                  <option value="">Sélectionner un artiste</option>
                  {practitioners
                    .filter(pract => !associatedPractitioners.some(ap => ap.practitionerId === pract.id))
                    .map(pract => (
                      <option key={pract.id} value={pract.id}>
                        {pract.firstName} {pract.lastName} ({pract.email})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Type de relation *
                </label>
                <select
                  value={newAssociation.relationshipType}
                  onChange={(e) => setNewAssociation(prev => ({
                    ...prev,
                    relationshipType: e.target.value as any
                  }))}
                  className="input-field"
                >
                  <option value="FREELANCE">Freelance</option>
                  <option value="EMPLOYEE">Employé</option>
                  <option value="RENTAL">Location</option>
                  <option value="PARTNERSHIP">Partenariat</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Tarif horaire spécifique (€)
                </label>
                <input
                  type="number"
                  min="0"
                  step="5"
                  value={newAssociation.hourlyRate}
                  onChange={(e) => setNewAssociation(prev => ({
                    ...prev,
                    hourlyRate: e.target.value
                  }))}
                  className="input-field"
                  placeholder="Optionnel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-textPrimary mb-2">
                  Taux de commission (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={newAssociation.commissionRate}
                  onChange={(e) => setNewAssociation(prev => ({
                    ...prev,
                    commissionRate: e.target.value
                  }))}
                  className="input-field"
                  placeholder="Optionnel"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddPractitionerModal(false);
                  setSelectedPractitionerId('');
                  setNewAssociation({
                    relationshipType: 'FREELANCE',
                    hourlyRate: '',
                    commissionRate: '',
                  });
                }}
                className="btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleAddPractitionerAssociation}
                disabled={!selectedPractitionerId}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save Button (Mobile) */}
      <div className="mt-8 flex flex-col items-end gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Sauvegarde...' : 'Sauvegarder'}</span>
        </button>
      </div>
    </div>
  );
}
